import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout, { useSEO } from "@/components/Layout";
import { Section, Overline, BlueRule, Button } from "@/components/primitives";
import PhilosophyFlow from "@/components/PhilosophyFlow";
import NewsletterSection from "@/components/NewsletterSection";

const PATHWAYS = [
  ["Strengthen My Mindset", "mindset"],
  ["Become More Disciplined", "discipline"],
  ["Build Greater Resilience", "resilience"],
  ["Become a Better Leader", "leadership"],
  ["Build Healthier Relationships", "relationships"],
  ["Live More Intentionally", "intentional-living"],
];

export default function StartHere() {
  useSEO({
    title: "Start Here | Impactful Mindset",
    description: "A guided introduction to what Impactful Mindset stands for—and where to begin.",
  });
  return (
    <Layout>
      <Section className="pt-20 pb-12">
        <div className="max-w-3xl">
          <Overline>Start Here</Overline>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4 leading-tight text-[#111827]">
            A grounded place to begin
          </h1>
          <BlueRule className="mt-6" />
          <p className="text-lg text-slate-600 mt-6 leading-relaxed">
            Impactful Mindset is a publishing and education platform for people who want to think more clearly,
            decide more wisely and live more intentionally. It is not about hype, quick fixes or forced positivity.
          </p>
        </div>
      </Section>

      <Section className="py-12 bg-[#F8F9FA]">
        <div className="max-w-3xl">
          <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold text-[#111827]">
            What a healthy mindset is—and isn't
          </h2>
          <p className="text-slate-600 mt-5 leading-relaxed">
            Developing a strong mindset does <strong>not</strong> mean forced positive thinking, denying difficult
            realities, or pretending problems do not exist. It means seeing clearly, thinking honestly, and choosing
            responses you can stand behind—even when things are hard.
          </p>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Real growth follows a sequence. Awareness comes first; behavior comes last.
          </p>
        </div>
        <div className="mt-10"><PhilosophyFlow /></div>
      </Section>

      <Section className="py-20">
        <Overline>Choose a Pathway</Overline>
        <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-3 text-[#111827]">Where would you like to begin?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
          {PATHWAYS.map(([label, slug]) => (
            <Link
              key={slug}
              to={`/ideas/${slug}`}
              data-testid={`pathway-${slug}`}
              className="group flex items-center justify-between border border-slate-200 p-6 bg-white hover:border-[#0F2C59] hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="font-serif-display text-xl font-semibold text-[#111827] group-hover:text-[#0F2C59] transition-colors">{label}</span>
              <ArrowRight className="text-[#0F2C59] group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          ))}
        </div>
        <div className="mt-12">
          <Button to="/join" data-testid="start-here-join">Join Impactful Mindset</Button>
        </div>
      </Section>

      <NewsletterSection />
    </Layout>
  );
}
